// PT-BR friendly error messages — the SINGLE abstraction for API error feedback.
// Never surface an axios stack or a raw English string to the user (FR31 / NFR10).
//
// Resolution order (most specific wins):
//   1. Backend structured `{code}` → CODE_MESSAGES (every backend code mapped).
//   2. Backend plain-string `detail` → shown as-is (the backend writes them in
//      PT-BR for the user, e.g. "E-mail ou senha inválidos").
//   3. HTTP status fallback → STATUS_MESSAGES.
//   4. Generic fallback.

const CODE_MESSAGES = {
  // Auth / sessão
  invalid_token: "Este link não é mais válido. Peça um novo.",
  invalid_current_password: "A senha atual não confere.",
  access_pending: "Sua conta está em análise. Avisaremos assim que liberar.",
  access_blocked: "Seu acesso está bloqueado no momento.",

  // Avatares / persona
  draft_exists: "Você já tem um rascunho de avatar em andamento.",
  draft_not_found: "Não encontramos esse rascunho.",
  immutable: "Esse campo não pode ser alterado em um avatar já concluído.",
  incomplete: "Ainda faltam campos obrigatórios para concluir.",
  invalid_matriz: "A matriz estratégica precisa de 10 itens em cada eixo.",
  confirm_required: "Digite RESETAR para confirmar.",
  cannot_self_reset: "Para refazer a sua própria Brand Persona, use Minha conta.",
  thread_archived: "Essa conversa é de antes do reset da Brand Persona e ficou só para leitura. Recarregue a página para continuar na conversa nova.",

  // Agentes / conteúdo
  agent_not_found: "Não encontramos esse agente.",
  artifact_not_found: "Não encontramos esse conteúdo.",
  avatar_required: "Escolha um avatar para usar este agente.",
  locked_plan: "Este agente não está incluído no seu plano atual.",
  image_extraction_failed: "Não consegui ler os números desse print. Tente uma imagem mais nítida.",
  title_required: "Dê um título antes de salvar.",

  // Calendário
  entry_not_found: "Não encontramos essa entrada do calendário.",

  // Colaboradores / workspace
  role_viewer: "Você está em modo leitura neste workspace — só o dono pode fazer alterações.",
  collaborator_limit: "Limite de 3 colaboradores atingido.",
  already_invited: "Esse e-mail já foi convidado.",
  self_invite: "Você não pode convidar a si mesma.",
  invalid_workspace: "Workspace inválido.",
  not_pending: "Esse convite já foi respondido.",
  resend_rate_limited: "Aguarde um instante antes de reenviar.",

  // Admin
  invalid_access_status: "Status de acesso inválido.",
  invalid_plan: "Plano inválido.",
  email_in_use: "Este e-mail já está em uso por outra conta.",
  reserved_admin_email: "Este e-mail é reservado para uma conta de administração.",
  invalid_name: "Informe um nome válido para a conta.",
  cannot_self_manage: "Para gerenciar sua própria conta, use Minha conta.",
  user_not_found: "Não encontramos esta conta.",
  cannot_impersonate_admin: "Não é possível atender temporariamente uma conta de admin.",
  target_access_blocked: "Esta conta está bloqueada e não pode ser atendida.",
  target_access_pending: "Libere esta conta antes de iniciar o atendimento temporário.",
  impersonation_restricted: "Esta ação não está disponível durante o atendimento temporário.",
  impersonation_invalid: "Este atendimento temporário não é mais válido. Você voltou ao admin.",
  session_revoked: "Esta sessão foi encerrada. Você voltou ao admin.",
  password_too_long: "A senha pode ter no máximo 72 bytes. Use menos caracteres ou evite emojis.",
  cannot_self_downgrade: "Você não pode remover o próprio acesso de admin.",
  no_changes: "Nada para alterar.",
  push_disabled: "Os avisos no celular ainda não estão disponíveis neste ambiente.",
  invalid_subscription: "Não consegui ligar o aviso neste aparelho. Tenta de novo.",
  forbidden: "Você não tem acesso a esta área.",

  // Voz
  session_active_elsewhere: "Você já tem uma conversa por voz ativa em outro dispositivo.",
};

const STATUS_MESSAGES = {
  400: "Não foi possível concluir. Revise os dados e tente de novo.",
  401: "Sua sessão expirou. Entre novamente para continuar.",
  403: "Você não tem acesso a esta área.",
  404: "Não encontramos o que você procurava.",
  409: "Já existe um item em andamento.",
  422: "Faltam informações para concluir esta ação.",
  429: "Muitas tentativas. Aguarde um instante e tente de novo.",
  500: "Tivemos um problema por aqui. Tente novamente em instantes.",
  502: "Serviço indisponível no momento. Tente novamente.",
  503: "Serviço indisponível no momento. Tente novamente.",
};

const GENERIC = "Algo não saiu como esperado. Tente novamente.";
const OFFLINE = "Sem conexão com o servidor. Verifique sua internet e tente de novo.";

// Backend auth.py /auth_routes emit these as plain-string 401 details. They are
// PT-BR but jargon — the status fallback is what the usuária should read.
const AUTH_DETAILS = new Set([
  "Token expirado",
  "Token inválido",
  "Sessão expirada",
  "Sessão inválida",
  "Não autenticado",
  "Usuário não encontrado",
]);

// Extract the backend structured `code`, tolerating both {detail:{code}} and
// {detail:"..."} / {code} shapes used across the API.
function extractCode(err) {
  const d = err?.response?.data?.detail ?? err?.response?.data;
  if (d && typeof d === "object" && d.code) return d.code;
  return null;
}

// Backend plain-string details are written in PT-BR for the end user
// ("E-mail ou senha inválidos", "Muitas tentativas de login…"). Surface them
// verbatim when they look like a human sentence (not HTML, not a dump).
function extractDetailMessage(err) {
  const d = err?.response?.data?.detail;
  if (typeof d !== "string") return null;
  const s = d.trim();
  if (!s || s.length > 200) return null;
  if (s.startsWith("<") || s.includes("Traceback") || s.includes("\n")) return null;
  return s;
}

export function friendlyError(err) {
  // Network / no response (backend down, CORS, offline).
  if (err && err.isAxiosError && !err.response) return OFFLINE;

  const code = extractCode(err);
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];

  const detail = extractDetailMessage(err);
  if (detail && AUTH_DETAILS.has(detail)) return STATUS_MESSAGES[401];
  if (detail) return detail;

  const status = err?.response?.status;
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  return GENERIC;
}

export function errorCode(err) {
  return extractCode(err);
}
